import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import usePageRoleConfig from 'v2/views/utils/usePageRoleConfig'

import { useUpdateNavigation } from 'data/hooks/navigation'
import { useStackPages } from 'data/hooks/pages'
import { useStackRoles } from 'data/hooks/roles'
import { useStackViews } from 'data/hooks/views'
import { NavigationAppMenuItem, NavigationAppMenuItemView } from 'features/navigation/types'

import { useToast } from 'ui/components/Toast'

const LOADING_SLOW_THRESHOLD_TIMEOUT = 2000
const MAX_ROLES_TO_SHOW = 99

type RoleOption = {
    label: string
    value: string
    isChecked: boolean
    canSeeAllPages: boolean
    isDisabled: boolean
}

export function useNavigationEditorItemVisibilityControlState(item: NavigationAppMenuItem) {
    const hasRoleRestrictionSupport = item.type === 'view' && !item.children?.length

    const pages: PageDto[] = useStackPages()
    const views: ViewDto[] = useStackViews()

    const targetPage = useMemo(() => {
        if (item.type !== 'view') return

        const targetView = views.find(({ url }) => url === item.path)
        const targetPage = pages.find((page) => page.options.view_id === targetView?._sid)

        return targetPage
        // @ts-expect-error: TS doesn't infer that item.type === 'view'
    }, [item.type, item.path, views, pages])

    const { setAndSavePageRoles, pageRoles, setPageRoles } = usePageRoleConfig(targetPage)
    const pageRolesRef = useRef(pageRoles)
    pageRolesRef.current = pageRoles

    let status: 'everyone' | 'restricted' | 'hidden' = 'everyone'
    if ((item as NavigationAppMenuItemView).hidden) {
        status = 'hidden'
    } else if (pageRoles && !pageRoles.includes('*') && hasRoleRestrictionSupport) {
        status = 'restricted'
    }

    const { data: roles } = useStackRoles()
    const roleOptions: RoleOption[] = useMemo(() => {
        const valueSet = new Set(pageRoles)

        return roles.reduce((acc, role) => {
            const canSeeAllPages = !!role.options?.all_pages

            let isChecked = false
            switch (status) {
                case 'everyone':
                    isChecked = true
                    break
                case 'restricted':
                    isChecked = valueSet.has(role._sid) || canSeeAllPages
                    break
            }

            const isDisabled = status !== 'restricted' || canSeeAllPages

            acc.push({
                label: role.label,
                value: role._sid,
                isChecked,
                canSeeAllPages,
                isDisabled,
            })

            return acc
        }, [] as RoleOption[])
    }, [roles, pageRoles, status])

    let label: string = 'Everyone'
    if (status === 'hidden') {
        label = 'Hidden'
    } else if (status === 'restricted') {
        // If there are more than MAX_ROLES_TO_SHOW roles, we'll show a truncated label.
        const roleCount = roleOptions.filter((role) => role.isChecked).length
        const formattedRoleCount =
            roleCount > MAX_ROLES_TO_SHOW ? `+${MAX_ROLES_TO_SHOW}+` : roleCount.toString()
        label = `${formattedRoleCount} role${roleCount === 1 ? '' : 's'}`
    }

    const startIcon = useMemo(() => {
        if (status === 'hidden') {
            return { name: 'EyeOff' } as const
        } else if (status === 'restricted') {
            return { name: 'Lock' } as const
        }

        return { name: 'Globe' } as const
    }, [status])

    const { mutateAsync: updateNavigation } = useUpdateNavigation()

    const [isSaving, setIsSaving] = useState(false)
    const [isSavingSlow, setIsSavingSlow] = useState(false)
    useEffect(() => {
        let timer: number

        if (isSaving) {
            timer = window.setTimeout(() => {
                setIsSavingSlow(true)
            }, LOADING_SLOW_THRESHOLD_TIMEOUT)
        } else {
            setIsSavingSlow(false)
        }

        return () => {
            window.clearTimeout(timer)
        }
    }, [isSaving])

    const toast = useToast()

    const onSetEveryone = useCallback(async () => {
        setIsSaving(true)

        try {
            await updateNavigation({
                id: item.id,
                patch: {
                    hidden: false,
                } as Partial<NavigationDto>,
            })

            setPageRoles(['*'])
            await setAndSavePageRoles(['*'])
        } catch {
            toast({
                type: 'error',
                title: 'There was a problem updating the navigation item',
                startIcon: { name: 'AlertCircle' },
                helperText: 'Please try again later. If the issue persists, contact support.',
            })
        } finally {
            setIsSaving(false)
        }
    }, [updateNavigation, item.id, setAndSavePageRoles, setPageRoles, toast])

    const onSetHidden = useCallback(async () => {
        setIsSaving(true)

        try {
            setPageRoles(['*'])

            await updateNavigation({
                id: item.id,
                patch: {
                    hidden: true,
                } as Partial<NavigationDto>,
            })

            await setAndSavePageRoles(['*'])
        } catch {
            toast({
                type: 'error',
                title: 'There was a problem updating the navigation item',
                startIcon: { name: 'AlertCircle' },
                helperText: 'Please try again later. If the issue persists, contact support.',
            })
        } finally {
            setIsSaving(false)
        }
    }, [item.id, setAndSavePageRoles, setPageRoles, toast, updateNavigation])

    const onSetRestricted = useCallback(async () => {
        setIsSaving(true)

        try {
            const existing = pageRolesRef.current as string[]
            const newRoles = existing.filter((r) => r !== '*')

            setPageRoles(newRoles)
            await setAndSavePageRoles(newRoles)

            if (status === 'hidden') {
                await updateNavigation({
                    id: item.id,
                    patch: { hidden: false } as Partial<NavigationDto>,
                })
            }
        } catch {
            toast({
                type: 'error',
                title: 'There was a problem updating the navigation item',
                startIcon: { name: 'AlertCircle' },
                helperText: 'Please try again later. If the issue persists, contact support.',
            })
        } finally {
            setIsSaving(false)
        }
    }, [item.id, setAndSavePageRoles, setPageRoles, status, toast, updateNavigation])

    const onToggleRole = useCallback(
        async (role: string, checked: boolean) => {
            setIsSaving(true)

            try {
                const existing = pageRolesRef.current as string[]
                const newRoles = checked ? [...existing, role] : existing.filter((r) => r !== role)

                setPageRoles(newRoles)
                await setAndSavePageRoles(newRoles)
            } catch {
                toast({
                    type: 'error',
                    title: 'There was a problem updating the navigation item',
                    startIcon: { name: 'AlertCircle' },
                    helperText: 'Please try again later. If the issue persists, contact support.',
                })
            } finally {
                setIsSaving(false)
            }
        },
        [setPageRoles, setAndSavePageRoles, toast]
    )

    const alwaysVisible = status !== 'everyone'

    const onStatusChange = useCallback(
        (newStatus: string) => {
            switch (newStatus) {
                case 'everyone':
                    onSetEveryone()
                    break
                case 'restricted':
                    onSetRestricted()
                    break
                case 'hidden':
                    onSetHidden()
                    break
            }
        },
        [onSetEveryone, onSetHidden, onSetRestricted]
    )

    return useMemo(
        () => ({
            status,
            label,
            isSavingSlow,
            hasRoleRestrictionSupport,
            startIcon,
            roleOptions,
            onToggleRole,
            alwaysVisible,
            onStatusChange,
        }),
        [
            status,
            label,
            isSavingSlow,
            hasRoleRestrictionSupport,
            startIcon,
            roleOptions,
            onToggleRole,
            alwaysVisible,
            onStatusChange,
        ]
    )
}
